import * as SentryReact from '@sentry/react'
import { AxiosRequestConfig } from 'axios'
import Bowser from 'bowser'
import { omit, pick } from 'lodash'

import { UserInfoType } from '#/store/hocs/withUser'
import { TourPayload } from '#/store/providers/Tour/types'
import { ResolvedPage } from '#/store/services/Pages/PagesService'
import procEnv, { getReleaseVersion, isLocal } from '#/utils/processEnvironment'

type HitSentryParams = { page: ResolvedPage }

export enum SentryEvent {
  PAGE = 'page',
  BROWSER = 'browser',
  FIND_MY_COW = 'find_my_cow',
  FORM = 'form',
  BROWSER_GUARD_BYPASSED = 'browser_guard_bypassed',
  TOUR = 'tour',
  SUSPENDED_OPENED = 'suspended_opened',
  DASHBOARD_TABS = 'dashboard_tabs',
  FLOAT_ACTIONS = 'float_actions',
  SEARCH_CLICK = 'search_click',
  SEARCH_OPEN = 'search_open',
  VIC_MESSAGE_SENT = 'vic_message_sent',
}

function hitPage({ page }: HitSentryParams) {
  metrics({
    attributes: { context: page.context, page: page.track, page_id: page.id },
    event: SentryEvent.PAGE,
  })
}

function hitBrowser() {
  const parser = Bowser.getParser(window.navigator.userAgent)

  const { browser, os, platform } = parser.getResult()

  if (!browser?.name) return

  metrics({
    attributes: {
      browser_name: browser.name,
      browser_version: browser.version,
      os_name: os.name,
      os_version: os.version,
      platform_type: platform.type,
      platform_vendor: platform.vendor,
    },
    event: SentryEvent.BROWSER,
  })
}

type HitFormParams = { formName: string; formType: string }

function hitForm({ formName, formType }: HitFormParams) {
  metrics({
    attributes: {
      form_name: formName,
      form_type: formType,
    },
    event: SentryEvent.FORM,
  })
}

type HitDashboardTabsParams = { tab?: string; filter?: string }

function hitDashboardTabs(attributes: HitDashboardTabsParams) {
  metrics({ attributes, event: SentryEvent.DASHBOARD_TABS })
}

type HitFloatActionsParams = { action?: string }

function hitFloatActions(attributes: HitFloatActionsParams) {
  metrics({ attributes, event: SentryEvent.FLOAT_ACTIONS })
}

type HitSearchParams = { resourceType: string; isPage: boolean; isRecent: boolean; link: string }

function hitSearch({ isPage, isRecent, link, resourceType }: HitSearchParams) {
  metrics({
    attributes: { is_page: isPage, is_recent: isRecent, link, resource_type: resourceType },
    event: SentryEvent.SEARCH_CLICK,
  })
}

function setUser(user: UserInfoType) {
  SentryReact.setUser({
    id: user.uuid,
    username: user.name,
  })

  SentryReact.setAttributes(
    pick(user, ['has_seen_onboarding', 'is_system_user', 'isSuspended', 'locale_text']),
  )

  SentryReact.setContext('Informações adicionais de usuário', omit(user, ['uuid', 'name', 'phone']))
}

type HitTourParams = { tour: TourPayload; state: 'started' | 'finished' | 'skipped' }

function hitTour({ state, tour }: HitTourParams) {
  metrics({ attributes: { id: tour?.id, state }, event: SentryEvent.TOUR })
}

function setFarm(farm: { code: number; name: string; slug: string }) {
  SentryReact.setAttributes({
    farm_code: farm.code,
    farm_name: farm.name,
    farm_slug: farm.slug,
  })
}

function captureInvalidURL(config: AxiosRequestConfig) {
  if (/\/(undefined|null)(\/|$|\?)/.test(config.url ?? '')) {
    Sentry.captureException(new Error(`URL inválida: ${config.url}`), {
      fingerprint: ['invalid-url-param', config.url ?? 'unknown'],
      level: 'warning',
      tags: { method: config.method, url: config.url },
    })
  }
}

/**
 * The function `initializeSentry` sets up Sentry error tracking with specific configurations for
 * different environments and error handling.
 * https://docs.sentry.io/platforms/javascript/guides/react/
 */
const init = () => {
  const VITE_SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

  if (!VITE_SENTRY_DSN || isLocal) return null

  const environment = procEnv()

  SentryReact.init({
    dsn: VITE_SENTRY_DSN,
    environment,
    integrations: [
      // Tentativa de ignorar erro 429, caso pare de exibir erros remover essa integração
      SentryReact.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: false,
      }),
      SentryReact.replayIntegration({
        blockAllMedia: false,
        maskAllInputs: false,
        maskAllText: false,
        networkDetailAllowUrls: [
          window.location.origin,
          '',
          /^https:\/\/api\.(chipinside|cowmed)\.com\.br/,
        ],
      }),
    ],
    release: getReleaseVersion(),
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
  })
}

type MetricsParams = {
  attributes?: SentryReact.metrics.MetricOptions['attributes']
  event: SentryEvent
}

export function metrics({ attributes, event }: MetricsParams) {
  SentryReact.metrics.count(event, 1, { attributes })
}

export const Sentry = {
  ...SentryReact,
  captureInvalidURL,
  hitBrowser,
  hitDashboardTabs,
  hitFloatActions,
  hitForm,
  hitPage,
  hitSearch,
  hitTour,
  init,
  metrics,
  setFarm,
  setUser,
}
